import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGetBroker, apiPostBroker, apiPatchBroker } from "../api";
import { useAuth } from "../components/AuthContext";

function ContactModal({ contact, companies, onSave, onClose }) {
  const [form, setForm] = useState({
    companyId: contact?.companyId || "",
    firstName: contact?.firstName || "",
    lastName: contact?.lastName || "",
    email: contact?.email || "",
    phone: contact?.phone || "",
    title: contact?.title || "",
    isPrimary: contact?.isPrimary || false,
    notes: contact?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.companyId || !form.firstName || !form.lastName) {
      alert("Company, first name, and last name are required");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "white", borderRadius: 12, padding: 24, width: "100%", maxWidth: 500, maxHeight: "90vh", overflow: "auto" }}>
        <h2 style={{ margin: "0 0 20px 0" }}>{contact ? "Edit Contact" : "New Contact"}</h2>
        <div style={{ display: "grid", gap: 16 }}>
          <div>
            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Company *</label>
            <select value={form.companyId} onChange={(e) => setForm({ ...form, companyId: e.target.value })} style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }}>
              <option value="">Select company...</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>First Name *</label>
              <input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }} />
            </div>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Last Name *</label>
              <input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }} />
            </div>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }} />
            </div>
          </div>
          <div>
            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Logistics Manager" style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }} />
          </div>
          <div>
            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 24, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "10px 20px", background: "white", border: "2px solid #e5e7eb", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: "10px 20px", background: "#DC2626", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>{saving ? "Saving..." : "Save Contact"}</button>
        </div>
      </div>
    </div>
  );
}

export default function CRMContacts() {
  const nav = useNavigate();
  const { adminEmail } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editContact, setEditContact] = useState(null);

  async function loadData() {
    setLoading(true);
    setErr("");
    try {
      const [contactsRes, shippersRes] = await Promise.all([
        apiGetBroker("/crm/contacts", { adminEmail }),
        apiGetBroker("/broker/shippers", { adminEmail }),
      ]);
      setContacts(contactsRes.contacts || []);
      setCompanies(shippersRes.shippers || []);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, [adminEmail]);

  const handleSave = async (form) => {
    try {
      if (editContact) {
        await apiPatchBroker(`/crm/contacts/${editContact.id}`, form, { adminEmail });
      } else {
        await apiPostBroker("/crm/contacts", form, { adminEmail });
      }
      setShowModal(false);
      setEditContact(null);
      loadData();
    } catch (e) {
      alert(e.message || "Error saving contact");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await fetch(`/api/crm/contacts/${id}`, { method: "DELETE", headers: { "x-admin-email": adminEmail } });
      loadData();
    } catch (e) {
      alert(e.message || "Error deleting contact");
    }
  };

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto", padding: 20 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 900 }}>📇 Contacts</h1>
        <p style={{ margin: "8px 0 0 0", color: "#6b7280" }}>Manage your CRM contacts</p>
      </div>

      {err && <div style={{ marginBottom: 16, padding: 12, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, color: "#dc2626" }}>{err}</div>}

      <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
        <button onClick={() => nav("/app/broker/crm")} style={{ padding: "10px 20px", background: "white", border: "2px solid #e5e7eb", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>← Back to CRM</button>
        <button onClick={() => { setEditContact(null); setShowModal(true); }} style={{ padding: "10px 20px", background: "#DC2626", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>+ New Contact</button>
        <button onClick={loadData} style={{ padding: "10px 20px", background: "white", border: "2px solid #e5e7eb", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>↻ Refresh</button>
      </div>

      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 16, padding: 16, background: "#f9fafb", fontWeight: 700, fontSize: 12, color: "#6b7280", textTransform: "uppercase" }}>
          <div>Name</div><div>Company</div><div>Contact</div><div>Status</div><div>Actions</div>
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>Loading...</div>
        ) : contacts.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>No contacts yet. Click "+ New Contact" to add one.</div>
        ) : (
          contacts.map((c) => (
            <div key={c.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 16, padding: 16, borderTop: "1px solid #f3f4f6", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600 }}>{c.firstName} {c.lastName}</div>
                <div style={{ fontSize: 13, color: "#6b7280" }}>{c.title || "—"}</div>
              </div>
              <div style={{ fontSize: 13 }}>{c.company?.name || "—"}</div>
              <div>
                <div style={{ fontSize: 13 }}>{c.email || "—"}</div>
                <div style={{ fontSize: 12, color: "#9ca3af" }}>{c.phone || ""}</div>
              </div>
              <div>
                <span style={{ fontSize: 11, padding: "4px 10px", borderRadius: 12, background: c.status === "ACTIVE" ? "#d1fae5" : "#f3f4f6", color: c.status === "ACTIVE" ? "#065f46" : "#6b7280", fontWeight: 600 }}>{c.status}</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => { setEditContact(c); setShowModal(true); }} style={{ padding: "6px 12px", background: "white", border: "1px solid #e5e7eb", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Edit</button>
                <button onClick={() => handleDelete(c.id)} style={{ padding: "6px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, cursor: "pointer", color: "#dc2626", fontWeight: 600 }}>Delete</button>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && <ContactModal contact={editContact} companies={companies} onSave={handleSave} onClose={() => { setShowModal(false); setEditContact(null); }} />}
    </div>
  );
}
